import { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { MotifButton, MotifIcon } from "@ey-xd/motif-react";
import { IconoirNavArrowDown, IconoirNavArrowUp } from "@ey-xd/motif-icon";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import Table from "@/components/Table/table";
import TitleComplement from "../statusTable/TitleComplement";
import CustomPagination from "@/components/Pagination/CustomPagination";
import { useGetPaymentStatusColumnDefs } from "../statusTable/PaymentStatusColumnDefinations";
import {
  useGetPaymentStatusListQuery,
  useLazyGetPaymentFilterQuery,
} from "@/services/paymentStatus/paymentStatusReducer";
import { useGetCalculationStepDataQuery } from "@/features/distributions/steps/calculationStepReducer";
import { Filter } from "./Filter";
import { FormValues } from "../type";
import DeletePayment from "./DeletePayment";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import "./Payments.scss";
import { useLazyDownloadDocumentQuery } from "@/services/documents/documentsReducer";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
import { commonFormateName } from "@/utility/common";
import { useLazyExportDocumentQuery } from "@/services/exports/exportsReducer";
import { GetCommonDownloadImgButton } from "@/components/Images/CommonImages";

const Payments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [showFilter, setShowFilter] = useState(false);
  const retainState = useSelector((e: any) => e.retainState.paymentsList);
  const isFilterActive = retainState.isFilterActive;
  const filterPayload = useMemo(
    () => retainState.filterPayload,
    [retainState.filterPayload]
  );
  const { caseShortName, abbreviation } = useSelector(
    (state: any) => state.case
  );
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [paymentSuccessMessage, setPaymentSuccessMessage] = useState<any>({
    show: false,
    type: "",
    message: "",
  });
  const [paymentId, setPaymentId] = useState("");
  const permissions = useSelector((state: any) => state.permissions);
  const { Payment, ImportJob } = permissions;
  let tableLoadingData = customTableLoadingData;
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  const dispatch = useDispatch();
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      CaseId: caseId,
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize || paginationPageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [caseId, paginationPageSize, retainState]);
  const paginationObj = getPaginationObj;
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<
    { paymentId: string }[]
  >([]);
  const [allPaymentSelected, setAllPaymentSelected] = useState(false);
  const [paymentReference, setPaymentReference] = useState<string>("");
  const {
    data: paymentStatusData,
    isLoading: isPaymentListLoading,
    isFetching: isPaymentListFetching,
    error: PaymentListError,
    status: paymentStatusStatus,
    refetch: refetchPaymentList,
  } = useGetPaymentStatusListQuery(paginationObj, { skip: isFilterActive });
  const [downloadDocument] = useLazyDownloadDocumentQuery();
  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "paymentsList",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }
  let resetSortStatus = useCallback(() => {
    let json: any = { ...retainState.sortedColumnTypes };
    for (const key in json) {
      json[key] = { sort: null };
    }
    return json;
  }, [retainState.sortedColumnTypes]);
  const [
    triggerPaymentFilter,
    {
      data: paymentFilterData,
      isFetching: filterFetching,
      error: filterError,
      status: paymentFilterStatus,
    },
  ] = useLazyGetPaymentFilterQuery();
  const { data: accountData } = useGetCalculationStepDataQuery({
    caseId: caseId,
  });
  const accountList =
    (accountData &&
      accountData?.map((item: any) => ({
        name: item.accountName || item.name,
        id: item.accountId || item.id,
      }))) ||
    [];
  const [formValues, setFormValues] = useState<FormValues>(
    retainState.formValues || {
      payeeName: "",
      accountName: "",
      prefix: "",
      paymentReferenceStartWith: "",
      paymentReferenceEndWith: "",
      issueStartDate: undefined,
      issueEndDate: undefined,
      lowerBound: "",
      upperBound: "",
      postedDateStart: undefined,
      postedDateEnd: undefined,
      paymentStatus: "",
    }
  );
  const tableDataFetching = isPaymentListFetching || filterFetching;
  const tableDataError = PaymentListError || filterError;
  const tableDataHasRecords = isFilterActive
    ? paymentFilterData?.hasMoreRecords
    : paymentStatusData?.hasMoreRecords;
  const tableData = isFilterActive
    ? paymentFilterData?.results
    : paymentStatusData?.results;
  const apiStatus = isFilterActive ? paymentFilterStatus : paymentStatusStatus;
  const apiRes = isFilterActive ? paymentFilterData : paymentStatusData;

  useEffect(() => {
    setSelectedPaymentIds([]);
    if (isFilterActive) {
      const pageData = {
        ...paginationObj,
        PageNumber: paginationObj.PageNumber,
      };
      triggerPaymentFilter({ ...filterPayload, ...pageData });
    } else {
      refetchPaymentList();
    }
  }, [
    paginationObj,
    filterPayload,
    refetchPaymentList,
    isFilterActive,
    triggerPaymentFilter,
  ]);

  const clearSelectedPayemntIds = () => {
    setSelectedPaymentIds([]);
  };
  const refreshAfterPaymentUpdate = (successMessage?: boolean) => {
    dispatch(
      setComponentState1({
        entityName: "paymentsList",
        entityState: {
          PageNumber: 1,
          PageSize: paginationPageSize,
          SearchText: "",
          sortedColumnTypes: { ...resetSortStatus() },
          SortOrder: "",
          SortColumn: "",
        },
      })
    );
    successMessage && fetchSuccessMessage();
  };
  function fetchSuccessMessage() {
    window.scrollTo(0, 400);
    setPaymentSuccessMessage({
      show: true,
      message: t("pages.paymentStatus.paymentStatusUpdate"),
      type: "success",
    });
  }
  const onRowSelected = (selectedRows: any) => {
    const selectedNode = selectedRows.node;
    const selectedData = selectedNode.data;
    const isSelected = selectedNode.isSelected();
    const { paymentId, paymentStatus, postedDate, issueDate } = selectedData;
    setSelectedPaymentIds((prevSelectedIds) =>
      isSelected
        ? [
            ...prevSelectedIds,
            { paymentId, paymentStatus, postedDate, issueDate },
          ]
        : prevSelectedIds.filter((item) => item.paymentId !== paymentId)
    );
  };
  const handleFilter = useCallback(
    (searchParams: any, formValues: any) => {
      dispatch(
        setComponentState1({
          entityName: "paymentsList",
          entityState: {
            PageNumber: 1,
            sortedColumnTypes: { ...resetSortStatus() },
            SortOrder: "",
            SortColumn: "",
            formValues: { ...formValues },
            filterPayload: { ...searchParams },
            isFilterActive: true,
          },
        })
      );
    },
    [resetSortStatus, dispatch]
  );
  const clearFilter = () => {
    setFormValues((prevValues: any) => ({
      ...prevValues,
      payeeName: "",
      accountName: "",
      prefix: "",
      paymentReferenceStartWith: "",
      paymentReferenceEndWith: "",
      issueStartDate: undefined,
      issueEndDate: undefined,
      lowerBound: "",
      upperBound: "",
      postedDateStart: undefined,
      postedDateEnd: undefined,
      paymentStatus: "",
      statusUpdateStartDate: undefined,
      statusUpdateEndDate: undefined,
    }));
    setShowFilter(false);

    dispatch(
      setComponentState1({
        entityName: "paymentsList",
        entityState: {
          PageNumber: 1,
          sortedColumnTypes: { ...resetSortStatus() },
          SortOrder: "",
          SortColumn: "",
          isFilterActive: false,
          formValues: null,
        },
      })
    );
  };
  const handleCellClick = (params: any) => {
    navigate(`/case/${caseId}/payment/${params.data?.paymentId}`);
  };
  const handleDeleteEvent = (paymentId: string, paymentReference: string) => {
    setShowDeletePopup(true);
    setPaymentId(paymentId);
    setPaymentReference(paymentReference);
  };
  let [documnetId, setDocumentId] = useState<any>("");
  const getPaymentStatusColumnDefs = useGetPaymentStatusColumnDefs(
    handleCellClick,
    handleDeleteEvent,
    Payment,
    caseId,
    documnetId
  );
  const [exportDocument, { isLoading: exportingFile }] =
    useLazyExportDocumentQuery();
  function handleExport() {
    let requestBodyData = { ...paginationObj };
    if (isFilterActive) {
      requestBodyData = {
        ...filterPayload,
        ...paginationObj,
        PageNumber: paginationObj.PageNumber,
      };
    }
    exportDocument({
      url: "Payment/ExportPaymentFilterList",
      fileName: caseShortName
        ? commonFormateName(caseShortName, "PaymentList", abbreviation)
        : "",
      ...requestBodyData,
    })
      .then((response) => {
        if (response?.error && "data" in response.error) {
          try {
            const errObj = JSON.parse(response?.error?.data as string);
            setDeleteErrorMessage(errObj?.message || errObj.title);
          } catch (error) {
            setDeleteErrorMessage("Something went wrong!!");
          }
        }
      })
      .catch((error: any) => {
        setDeleteErrorMessage("Something went wrong!!");
      });
  }
  const handleAllPaymentSelected = (isSelected: boolean) => {
    setAllPaymentSelected(isSelected);
  };
  const onSelectionChanged = (event: any) => {
    const allSelected =
      event.api.getSelectedNodes().length === event.api.getDisplayedRowCount();
    handleAllPaymentSelected(allSelected);
  };
  const actionRenderers = {
    downloadCheck: function (params: any) {
      return params?.data?.documentId ? (
        <GetCommonDownloadImgButton
          onClick={() => {
            setDocumentId(params.data.documentId);
            downloadDocument({
              caseId: caseId,
              documentId: params.data.documentId,
              fileName: params?.data?.fileName,
            })
              .then(() => {
                setDocumentId(null);
              })
              .catch(() => {
                setDocumentId(null);
              });
          }}
          loading={params?.selectedDoumnetId === params.data.documentId}
        ></GetCommonDownloadImgButton>
      ) : (
        <></>
      );
    },
  };
  if (isPaymentListLoading) {
    return <div>{t("global.globalNotifications.loadingMessage")}</div>;
  }
  return (
    <div>
      <div className="mar-b-20">
        <div className="motif-table-title-bar payment-filter-con ">
          <div className="motif-table-info">
            <span className="motif-table-title">
              {t("pages.paymentStatus.filterForm.title")}
            </span>
            <span className="motif-table-description">
              {t("pages.paymentStatus.filterForm.description")}
            </span>
          </div>
          <MotifButton
            type="button"
            onClick={() => {
              setShowFilter((prev) => !prev);
              setPaymentSuccessMessage &&
                setPaymentSuccessMessage({
                  show: false,
                  message: "",
                  type: "",
                });
            }}
            variant="secondary"
            size="small"
          >
            {t("pages.paymentStatus.filterForm.filterButton")}
            <MotifIcon
              aria-label="Dropdown icon"
              fill="none"
              iconFunction={
                showFilter ? IconoirNavArrowUp : IconoirNavArrowDown
              }
            />
          </MotifButton>
        </div>
        {showFilter && (
          <Filter
            accountList={accountList}
            setShowFilter={setShowFilter}
            onApply={handleFilter}
            formValues={formValues}
            setFormValues={setFormValues}
            clearFilter={clearFilter}
          />
        )}
      </div>
      {deleteErrorMessage && (
        <div className="mar-t-20">
          <NotificationGrid
            show={true}
            message={deleteErrorMessage}
            type={"error"}
            onClose={() => setDeleteErrorMessage("")}
          />
        </div>
      )}

      {paymentSuccessMessage.show && (
        <div className="mar-t-20">
          <NotificationGrid
            show={paymentSuccessMessage.show}
            message={paymentSuccessMessage.message}
            type={paymentSuccessMessage.type}
            onClose={() => setPaymentSuccessMessage({show: false, message: "", type: ""})}
            scrollToTop={false}
          />
          <br></br>
        </div>
      )}
      <Table
        columnDefs={getPaymentStatusColumnDefs}
        paginationObj={paginationObj}
        caseRenderers={actionRenderers}
        columnTypes={{ ...retainState.sortedColumnTypes }}
        handlePaginationData={handlePaginationData}
        cellRenderers={tableDataFetching ? [] : tableData ? tableData : []}
        TitleComplement={() => (
          <TitleComplement
            selectedPaymentIds={selectedPaymentIds}
            clearSelectedPayemntIds={clearSelectedPayemntIds}
            refreshAfterPaymentUpdate={refreshAfterPaymentUpdate}
            clearFilter={clearFilter}
            isFilterActive={isFilterActive}
            allPaymentSelected={allPaymentSelected}
            filterPayload={filterPayload}
            Payment={Payment}
            ImportJob={ImportJob}
            handleExport={handleExport}
            exportingFile={exportingFile}
            setPaymentSuccessMessage={setPaymentSuccessMessage}
          />
        )}
        title={"pages.paymentStatus.statusGridTitle"}
        description={"pages.paymentStatus.statusGridDescription"}
        overlayNoRowsTemplate={
          tableDataFetching
            ? t(tableLoadingData)
            : tableDataError
            ? t("pages.paymentStatus.somethingWentWrong")
            : t("pages.paymentStatus.noRecord")
        }
        rowSelection="multiple"
        onRowSelected={onRowSelected}
        pagination={false}
        onSelectionChanged={onSelectionChanged}
      />
      <CustomPagination
        apiData={apiRes}
        apiStatus={apiStatus}
        currentpage={paginationObj.PageNumber}
        pageSize={paginationObj.PageSize}
        disableNext={!tableDataHasRecords}
        onChangePageSize={(page: number) => {
          dispatch(
            setComponentState1({
              entityName: "paymentsList",
              entityState: {
                PageSize: page,
                PageNumber: 1,
              },
            })
          );
        }}
        onChangeNextPage={() => {
          clearSelectedPayemntIds();

          dispatch(
            setComponentState1({
              entityName: "paymentsList",
              entityState: {
                PageNumber: paginationObj.PageNumber + 1,
              },
            })
          );
        }}
        onChangePreviousPage={() => {
          clearSelectedPayemntIds();

          dispatch(
            setComponentState1({
              entityName: "paymentsList",
              entityState: {
                PageNumber: paginationObj.PageNumber - 1,
              },
            })
          );
        }}
        pageSizeSelector={paginationPageSizeSelector}
      />
      {showDeletePopup && (
        <DeletePayment
          show={showDeletePopup}
          setShow={setShowDeletePopup}
          id={paymentId}
          setDeleteErrorMessage={setDeleteErrorMessage}
          refetchPaymentList={refreshAfterPaymentUpdate}
          paymentReference={paymentReference}
        />
      )}
    </div>
  );
};
export default Payments;
